/**
 * DOSE waitlist form — vanilla JS port of the interactive bits of the
 * Next.js site's Waitlist() component (app/page.tsx). No React, no
 * sessionStorage bridge: submit adds to Shopify's own cart (name/email/
 * promo/referral ride along as cart line-item properties — see the
 * `properties[...]` input names in sections/dose-hero.liquid) and lands the
 * customer on the product page rather than jumping straight to checkout,
 * so they see it's in their cart before deciding to pay — the device isn't
 * actually reserved until checkout completes. Checkout, order, and refund
 * records are Shopify's from there on.
 *
 * Deliberately NOT ported from the old site: the Web3Forms lead-email POST
 * and its honeypot fields (there's no separate "submit a lead" step to spam
 * anymore — the only submit path is a real Shopify checkout), the custom
 * GA4 Measurement Protocol call (Shopify has its own native analytics/Web
 * Pixels), and the ?checkout=success return-trip success panel (Shopify's
 * own Order Status / Thank You page replaces it — see the plan's open item
 * on Order status page scripting).
 */
;(function () {
  'use strict'

  /* ── Product-page state, driven by the REAL cart (not a ?reserved=1 URL
     param — that seemed simpler, but Shopify's /discount/<code>?redirect=X
     endpoint strips X's own query string when it redirects, so the flag
     never survives a redirect that carries a promo code). Checking
     /cart.js instead is robust regardless of which path got them here, and
     naturally covers a customer returning to this page later too:
       - item_count > 0 → this item is ALREADY in the cart, so: show the
         confirmation banner (worded as "in your cart", NOT "reserved" —
         that's only true once checkout completes), and swap the normal
         add-to-cart-then-checkout form for a plain "Continue to Checkout"
         link so the button can't add a SECOND unit on top.
       - total_discount > 0 → a real Shopify discount code is applied to
         the session (via the /discount/<code> redirect below); show the
         actual saved amount, not a re-guessed cosmetic preview.
     Independent of the form guard below since this runs on the PRODUCT
     page, not the homepage. ── */
  const reservedBanner = document.querySelector('[data-reserved-banner]')
  const buyForm = document.querySelector('.product-main-form')
  const continueLink = document.querySelector('[data-continue-checkout]')
  const discountEl = document.querySelector('[data-product-discount]')
  const discountAmountEl = document.querySelector('[data-product-discount-amount]')
  if (reservedBanner || discountEl) {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        if (cart.item_count > 0) {
          if (reservedBanner) reservedBanner.hidden = false
          if (buyForm) buyForm.hidden = true
          if (continueLink) continueLink.hidden = false
        }
        if (cart.total_discount > 0 && discountEl && discountAmountEl) {
          const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: cart.currency || 'USD' })
          discountAmountEl.textContent = '−' + money.format(cart.total_discount / 100)
          discountEl.hidden = false
        }
      })
      .catch((err) => console.error('[dose-waitlist] cart state check failed:', err))
  }

  const form = document.getElementById('dose-waitlist-form')
  if (!form) return

  const configEl = document.getElementById('dose-waitlist-config')
  const config = configEl ? JSON.parse(configEl.textContent) : {}
  const promoCodes = (config.promoCodes || []).map((c) => c.toUpperCase())

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  /* ── Spots-remaining counter: real inventory rendered server-side by
     Liquid (data-spots-remaining), animated the same easeOutQuart way the
     old site animated its /api/spots fetch result. No fetch needed here —
     Shopify's inventory count IS the number, already in the page. ── */
  const counterEl = form.closest('.card')?.querySelector('[data-spots-remaining]')
  if (counterEl) {
    const target = Math.max(0, parseInt(counterEl.dataset.spotsRemaining, 10) || 0)
    const ms = 1800
    let start = null
    const tick = (now) => {
      if (start === null) start = now
      const p = Math.min((now - start) / ms, 1)
      const eased = 1 - Math.pow(1 - p, 4)
      counterEl.textContent = Math.floor(eased * target).toLocaleString('en-US')
      if (p < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }


  /* ── Promo apply (cosmetic check, same pattern as the old Stripe site: the
     REAL discount is a Shopify discount code, applied via /discount/<code>
     at checkout — kept in sync manually with promoCodes above). ── */
  const promoInput = form.querySelector('[data-promo-input]')
  const promoApplyBtn = form.querySelector('[data-promo-apply]')
  const promoMsg = form.querySelector('[data-promo-msg]')
  const pbTotal = form.querySelector('[data-pb-total]')
  const pbCompareAt = form.querySelector('[data-pb-compare-at]')
  const pbDiscountRow = form.querySelector('[data-pb-discount-row]')
  const pbDiscount = form.querySelector('[data-pb-discount]')
  let appliedPromoCode = ''

  /* Referral code from URL (?ref / ?referral / ?r) prefills the same optional
     field — there's no separate dedicated referral input anymore. */
  const urlParams = new URLSearchParams(window.location.search)
  const refFromUrl = urlParams.get('ref') || urlParams.get('referral') || urlParams.get('r')
  if (refFromUrl && promoInput && !promoInput.value) promoInput.value = refFromUrl

  function setPromoMsg(text, ok) {
    promoMsg.textContent = text
    promoMsg.className = 'promo-msg' + (text ? (ok ? ' promo-success' : ' promo-error') : '')
  }

  function applyPromoDisplay() {
    if (pbTotal) pbTotal.textContent = config.promoPriceDisplay
    if (pbDiscount) pbDiscount.textContent = config.promoDiscountDisplay
    if (pbDiscountRow) pbDiscountRow.hidden = false
    if (pbCompareAt) {
      if (!pbCompareAt.textContent.trim()) pbCompareAt.textContent = config.compareAtDisplay
      pbCompareAt.hidden = false
    }
  }

  function revertPromoDisplay() {
    if (pbTotal) pbTotal.textContent = pbTotal.dataset.original
    // Only re-hide the discount/compare-at rows if they weren't already
    // showing a REAL Shopify compare-at price before any promo was tried.
    if (pbDiscountRow && pbDiscountRow.dataset.realDiscount !== 'true') pbDiscountRow.hidden = true
    if (pbCompareAt && pbCompareAt.dataset.realDiscount !== 'true') pbCompareAt.hidden = true
  }

  // Remember whether the discount/compare-at rows were server-rendered from
  // a real Shopify compare-at price, so reverting an invalid promo doesn't
  // hide a discount that's actually real.
  if (pbDiscountRow) pbDiscountRow.dataset.realDiscount = pbDiscountRow.hidden ? 'false' : 'true'
  if (pbCompareAt) pbCompareAt.dataset.realDiscount = pbCompareAt.hidden ? 'false' : 'true'

  function applyPromo() {
    const raw = (promoInput.value || '').trim().toUpperCase()
    if (!raw) {
      setPromoMsg('Enter a code first.', false)
      return
    }
    if (promoCodes.includes(raw)) {
      appliedPromoCode = raw
      setPromoMsg('✓ Code applied — additional discount added', true)
      applyPromoDisplay()
    } else {
      appliedPromoCode = ''
      setPromoMsg('Invalid code. Please check and try again.', false)
      revertPromoDisplay()
    }
  }

  promoApplyBtn?.addEventListener('click', applyPromo)
  promoInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      applyPromo()
    }
  })

  /* ── Consent gates the submit button, exactly like the old site ── */
  const consentCheck = form.querySelector('[data-consent-check]')
  const submitBtn = form.querySelector('[data-submit-btn]')
  consentCheck?.addEventListener('change', () => {
    submitBtn.disabled = !consentCheck.checked
    submitBtn.title = consentCheck.checked ? '' : 'Please agree to the terms above to continue'
  })

  /* ── Submit: validate, add to cart via the Ajax Cart API, then go straight
     to checkout — through /discount/<code> first if a promo validated. ── */
  const formError = form.querySelector('[data-form-error]')
  const submitLabel = form.querySelector('[data-submit-label]')
  const firstNameInput = document.getElementById('fn')
  const lastNameInput = document.getElementById('ln')
  const emailInput = document.getElementById('em')

  function showFormError(msg) {
    formError.textContent = msg
    formError.hidden = false
  }

  const submitLabelIdle = submitLabel.innerHTML

  function setSubmitting(state) {
    submitBtn.disabled = state || !consentCheck.checked
    submitLabel.innerHTML = state
      ? '<svg width="13" height="13" viewBox="0 0 15 15" fill="none" style="animation:spin .8s linear infinite">' +
        '<circle cx="7.5" cy="7.5" r="5.5" stroke="rgba(255,255,255,0.22)" stroke-width="2" />' +
        '<path d="M13 7.5A5.5 5.5 0 0 1 2 7.5" stroke="white" stroke-width="2" stroke-linecap="round" /></svg>' +
        'Securing your spot…'
      : submitLabelIdle
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    formError.hidden = true

    if (!firstNameInput.value.trim() || !lastNameInput.value.trim()) {
      showFormError('Please enter your first and last name.')
      return
    }
    if (!isValidEmail(emailInput.value.trim())) {
      showFormError('Please enter a valid email address.')
      return
    }
    if (!consentCheck.checked) return // button is disabled in this state; belt and braces

    setSubmitting(true)
    try {
      const body = new FormData(form)
      const res = await fetch(config.cartAddUrl + '.js', { method: 'POST', body })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.description || 'Could not add to cart')
      }
      // Straight to checkout — the product page is no longer a step in the
      // reserve flow. checkoutUrl is built from routes.root_url so it keeps any
      // locale/market prefix instead of hardcoding '/checkout'. Nothing needs
      // to survive the /discount/<code> redirect below, since the code is
      // applied to the session rather than passed along as a query string.
      const target = config.checkoutUrl
      const dest = appliedPromoCode
        ? `/discount/${encodeURIComponent(appliedPromoCode)}?redirect=${encodeURIComponent(target)}`
        : target
      window.location.assign(dest)
    } catch (err) {
      setSubmitting(false)
      showFormError('Something went wrong reserving your spot. Please try again.')
      console.error('[dose-waitlist] add to cart failed:', err)
    }
  })
})()
